import "./index.css";

import React from 'react';
import DialogDataComponent, {executeComponentCallback} from 'Core/components/DialogDataComponent';
import PropTypes from "prop-types";
import {connect} from 'react-redux';
import {reducerStoreKey as campaignReducerStoreKey} from 'Pages/apps/default/projectPages/campaign/reducer';
import {selectors} from 'Core/store/reducers';
import * as pageConfig from '../../config';
import {saveDynamicCampaignScheduleAction} from 'Pages/apps/default/projectPages/campaign/actions';
import {getGlobalActions} from 'Core/helpers/redux';
import FormWrapper, {FormField} from 'Core/components/advanced/FormWrapper';
import Label from 'Core/components/display/Label';
import IoTimezoneSelectInput from 'Components/input/IoTimezoneSelectInput';
import {FORM_FIELD_LABEL_POSITION} from 'Core/components/advanced/FormWrapper/FormField';
import SelectInput from 'Core/components/input/SelectInput';
import {
	SCHEDULE_INTERVAL_TYPE,
	SCHEDULE_INTERVAL_TYPES
} from 'Pages/apps/default/projectPages/campaign/pages/ItemPage/const';
import YearDaySelectInput from 'Core/components/input/YearDaySelectInput';
import {
	getScheduleIntervalDescription,
	getTabTitle
} from 'Pages/apps/default/projectPages/campaign/pages/ItemPage/popups/MessageDefinitionPopup/helper';
import {
	icon_font_close_symbol,
	icon_font_save_symbol,
	icon_font_stack_class
} from 'Config/app';
import Icon from 'Core/components/display/Icon';
import CheckboxListInput, {CHECKBOX_LIST_INPUT_LAYOUT} from 'Core/components/input/CheckboxListInput';
import RadioInput from 'Core/components/input/RadioInput';
import {BUTTON_STYLE} from 'Core/components/display/Button';
import {isMacintosh} from 'Core/helpers/system';
import {getString, isset} from 'Core/helpers/data';
import {CAMPAIGN_STATUS} from 'Pages/apps/default/projectPages/campaign/const';
import {getTimeString} from 'Core/helpers/datetime';
import DayTimeInput from 'Components/input/DayTimeInput';

/**
 * Get all actions used by this component
 * @type {Object}
 */
const allActions = getGlobalActions({saveDynamicCampaignScheduleAction});

/**
 * Get state props from Redux store
 * @param {Object} state - Redux store state
 * @return {Object}
 */
const mapStateToProps = state => ({
	campaign: selectors[campaignReducerStoreKey].getCampaignItem(state),
});

class DynamicCampaignScheduleDialog extends DialogDataComponent {
	constructor(props) {
		super(props, {
			/** @type {DynamicScheduleDataObject} */
			data: props.scheduleData,
		}, {
			translationPath: 'DynamicCampaignSchedule',
			domPrefix: 'campaign-schedule-dialog',
			disableLoad: true,
			optimizedUpdate: true,
			optimizedUpdateIgnoreProps: ['onClose', 'onChange', 'onSave', 'onDelete', ...Object.keys(allActions)],
		});
		
		// Render methods
		this.renderReadOnly = this.renderReadOnly.bind(this);
	}
	
	
	// Action methods ---------------------------------------------------------------------------------------------------
	/**
	 * Validates and saves the data and triggers component's onSave event with both request and response data
	 */
	save() {
		const {dialogGUIID, saveDynamicCampaignScheduleAction} = this.props;
		const data = this.getDataToReturn();

		// Do the validation
		const isValid = this.validate();

		// If validation is successful
		if (isValid) {
			this.executeAbortableAction(saveDynamicCampaignScheduleAction, data)
				.then(res => {
					if (!!res) {
						this.close();
						executeComponentCallback(this.props.onSave, data, res, dialogGUIID);
					}
				});
		}
	}


	// Render methods ---------------------------------------------------------------------------------------------------
	/** @inheritDoc */
	renderDataActionButtons({
		saveLabel = undefined, saveIcon = undefined, closeLabel = undefined, closeIcon = undefined,
	} = {}, hideClose = false) {
		const {readOnly} = this.props;
		const autofocus = this.getOption('autoFocusActionButtons');

		let buttons = [];

		// Close/cancel button
		if (isMacintosh() && !hideClose) buttons.push({
			style: BUTTON_STYLE.DEFAULT,
			label: (closeLabel ? closeLabel : this.t('Close', 'general')),
			icon: (isset(closeIcon) && closeLabel !== null ? closeIcon : icon_font_close_symbol),
			onClick: this.close,
		});

		if (!readOnly) {
			buttons.push({
				style: BUTTON_STYLE.SUCCESS,
				label: (saveLabel ? saveLabel : this.t('Save', 'general')),
				icon: (isset(saveIcon) && saveIcon !== null ? saveIcon : icon_font_save_symbol),
				onClick: this.save,
				autofocus,
			});
		}

		// Close/cancel button
		if (!isMacintosh() && !hideClose) buttons.push({
			style: BUTTON_STYLE.DEFAULT,
			label: (closeLabel ? closeLabel : this.t('Close', 'general')),
			icon: (isset(closeIcon) && closeLabel !== null ? closeIcon : icon_font_close_symbol),
			onClick: this.close,
		});

		return this.renderActionButtons(buttons);
	}

	/**
	 * Renders the read-only view of the component
	 * @return {JSX.Element} The JSX element representing the read-only view.
	 */
	renderReadOnly() {
		const {campaign} = this.props;
		/** @type {DynamicScheduleDataObject} */
		const data = this.getData();
		const campaignLaunched = (
			getString(this.props, 'campaign.status') === CAMPAIGN_STATUS.ACTIVE ||
			getString(this.props, 'campaign.status') === CAMPAIGN_STATUS.STOPPED
		);

		return this.renderDialog(
			this.renderTitle(
				(
					<div className="campaign-item-dialog-title">
						<Label element="small" content={getTabTitle(campaign)} />
						<Icon
							className="title-icon"
							symbol={['calendar-o', 'clock-o']}
							stackClassName={`${icon_font_stack_class} schedule`}
						/>
						<div className="title-content">
							<Label
								element="div"
								content={this.t(
									'pings_schedule_section',
									'AppsSection.DefaultApp.projectPages.CampaignPage.ItemPage'
								)}
							/>
						</div>
					</div>
				)
			),
			(
				<FormWrapper className="dialog-form">
					{campaignLaunched ?
						<>
							<div className="notice success">
								<Label
									content={
										this.t('publishedCampaignNotice', `${pageConfig.translationPath}.MessageDefinitionPopup`)
									}
								/>
							</div>
							<br/>
						</>
						: null
					}
					
					<FormField label={this.t('timeZoneIdField')} labelPosition={FORM_FIELD_LABEL_POSITION.STACKED}>
						<IoTimezoneSelectInput readOnly={true} value={data.timeZoneId} />
					</FormField>
					
					<FormField 
						label={this.t(
							'pings_schedule_section',
							'AppsSection.DefaultApp.projectPages.CampaignPage.ItemPage'
						)} 
						labelPosition={FORM_FIELD_LABEL_POSITION.STACKED}
					>
						{!!data.intervalType ?
							getScheduleIntervalDescription(data.intervalType, data.days, data.time) :
							<Label content={this.t('notSpecified')} />
						}
					</FormField>
				</FormWrapper>
			)
		);
	}
	
	render() {
		const {readOnly, campaign} = this.props;
		/** @type {DynamicScheduleDataObject} */
		const data = this.getData();
		

		if (readOnly) return this.renderReadOnly();
		return this.renderDialog(
			this.renderTitle(
				(
					<div className="campaign-item-dialog-title">
						<Label element="small" content={getTabTitle(campaign)} />
						<Icon 
							className="title-icon"
							symbol={['calendar-o', 'clock-o']} 
							stackClassName={`${icon_font_stack_class} schedule`}
						/>
						<div className="title-content">
							<Label
								element="div"
								content={this.t(
									'pings_schedule_section', 
									'AppsSection.DefaultApp.projectPages.CampaignPage.ItemPage'
								)}
							/>
						</div>
					</div>
				)
			), 
			(
				<FormWrapper className="dialog-form">
					<FormField
						label={this.t('timeZoneIdField')}
						labelPosition={FORM_FIELD_LABEL_POSITION.STACKED}
						errorMessages={this.getValidationErrors('timeZoneId')}
					>
						<IoTimezoneSelectInput
							value={data.timeZoneId}
							onChange={tz => this.handleValueChange('timeZoneId', tz)}
						/>
					</FormField>
	
					<FormField
						label={this.t('intervalTypeField')}
						labelPosition={FORM_FIELD_LABEL_POSITION.STACKED}
						errorMessages={this.getValidationErrors('intervalType')}
					>
						<RadioInput
							isClearable={false}
							layout={CHECKBOX_LIST_INPUT_LAYOUT.ALIGNED}
							value={data.intervalType}
							buttonProps={{
								displayStyle: BUTTON_STYLE.DEFAULT,
							}}
							options={SCHEDULE_INTERVAL_TYPES.map(o => ({
								label: this.t(o, 'constants.schedule.interval_type'), value: o
							}))}
							onChange={v =>
								this.setValue('days', [])
									.then(() => this.handleValueChange('intervalType', v))
									.then(() => this.clearValidationErrors('days'))
							}
						/>
					</FormField>
	
					{
						SCHEDULE_INTERVAL_TYPES.includes(data.intervalType) ?
							<FormField
								className="days-field"
								label={this.t('daysField')}
								labelPosition={FORM_FIELD_LABEL_POSITION.STACKED}
								errorMessages={this.getValidationErrors('days')}
							>
								{
									data.intervalType === SCHEDULE_INTERVAL_TYPE.DATE_OF_WEEK ?
										<CheckboxListInput
											isClearable={true}
											layout={CHECKBOX_LIST_INPUT_LAYOUT.ALIGNED}
											value={data.days}
											clearValue={[]}
											options={[...Array(7).keys()].map(k => `${k + 1}`).map(d => ({
												label: this.translatePath(`constants.days.${d}.short`),
												value: d,
											}))}
											onChange={v => this.handleValueChange('days', v)}
										/>
									: data.intervalType === SCHEDULE_INTERVAL_TYPE.DATE_OF_MONTH ?
										<SelectInput
											isMulti={true}
											isClearable={true}
											options={[...Array(31).keys()].map(k => `${k + 1}`).map(d => (
												{label: `${d}.`, value: d}
											))}
											value={data.days}
											onChange={v => this.handleValueChange('days', v)}
											placeholder={this.t('Choose month days ...')}
										/>
									: data.intervalType === SCHEDULE_INTERVAL_TYPE.DATE_OF_YEAR ?
										<YearDaySelectInput
											isMulti={true}
											isClearable={true}
											value={data.days}
											onChange={v => this.handleValueChange('days', v)}
										/>
									: null
								}
							</FormField>
							: null
					}
					{
						SCHEDULE_INTERVAL_TYPES.includes(data.intervalType) ?
							<FormField
								className="time-of-day-field"
								label={this.t('timeField')}
								labelPosition={FORM_FIELD_LABEL_POSITION.STACKED}
								errorMessages={this.getValidationErrors('time')}
							>
								<DayTimeInput
									timeFormat="HH:mm:ss"
									value={data.time}
									onChange={v => this.handleValueChange('time', getTimeString(v))}
								/>
							</FormField>
							: null
					}
				</FormWrapper>
			)
		);
	}
}

/**
 * Define the component's own props that can be passed to it by parent components
 */
DynamicCampaignScheduleDialog.propTypes = {
	readOnly: PropTypes.bool,
	/** @type {DynamicScheduleDataObject} */
	scheduleData: PropTypes.object.isRequired,

	onClose: PropTypes.func,
	/** @type {Function<data: DynamicScheduleDataObject, savedData: DynamicScheduleDataObject, dialogGUIID: string>} */
	onSave: PropTypes.func,
};

export default connect(mapStateToProps, allActions, null, {forwardRef: true})(DynamicCampaignScheduleDialog);